import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import {
  PROFILE_DEMO_ENABLED,
  ProfileSampleSource,
  fillOrganization,
  noteProfileSample,
  rememberServices,
  rememberTeam,
  rememberWrite,
  seedCapabilities,
  seedPortfolio,
  seedRatings,
  seedTeamFunctions,
} from './profile-demo-backend';
import { INVENTED_DATA_ENABLED } from './invented-data';

/**
 * Stands in for what Organization › Profile asks and `backend/api` cannot answer yet. The rules and
 * every seed live in {@link ./profile-demo-backend.ts}; this file is only the wiring.
 *
 * **To remove it:** delete the three `profile-demo-backend*` files and the word
 * `profileDemoBackendInterceptor` from the `withInterceptors([...])` list in `app.config.ts`.
 */

/** The only pages this may touch. Segment match: `/organizationnewer` must never qualify. */
const PROFILE_ROUTE = '/organizationnew';

export function onProfileRoute(routerUrl: string): boolean {
  const path = routerUrl.split(/[?#]/)[0];
  return path === PROFILE_ROUTE || path.startsWith(`${PROFILE_ROUTE}/`);
}

/** What a request is, for this interceptor. `null` = not ours, pass straight through. */
export type ProfileKind =
  | { type: 'new-read'; source: ProfileSampleSource; seed: () => any }
  | { type: 'new-write'; path: string }
  | { type: 'fill-organization'; orgId: number }
  | { type: 'observe-services' }
  | { type: 'observe-team'; orgId: number }
  | { type: 'observe-public'; orgId: number };

/** Classifies a request by method and path. Exported for the spec. */
export function classify(req: HttpRequest<unknown>): ProfileKind | null {
  let path: string;
  try {
    path = new URL(req.url, 'http://x').pathname;
  } catch {
    path = req.url.split('?')[0];
  }
  const get = req.method === 'GET';
  let m: RegExpMatchArray | null;

  // ── NEW reads
  if (get && (m = path.match(/\/organization\/(\d+)\/capabilities$/))) return { type: 'new-read', source: 'capabilities', seed: () => seedCapabilities() };
  if (get && /\/ratings\/organizations\/\d+\/summary$/.test(path)) return { type: 'new-read', source: 'ratings', seed: seedRatings };
  if (get && /\/organization\/\d+\/portfolio$/.test(path)) return { type: 'new-read', source: 'portfolio', seed: seedPortfolio };
  if (get && (m = path.match(/\/organization\/(\d+)\/teamByFunction$/))) {
    const orgId = Number(m[1]);
    return { type: 'new-read', source: 'team-functions', seed: () => seedTeamFunctions(orgId) };
  }

  // ── NEW write: every profile field the organization write does not take yet
  if (req.method === 'PUT' && (m = path.match(/\/organization\/(\d+)\/profile$/))) return { type: 'new-write', path: `/organization/${m[1]}/profile` };

  // ── LIVE: fill absent keys, observe answers the seeds build on
  if (get && (m = path.match(/\/organization\/(\d+)$/))) return { type: 'fill-organization', orgId: Number(m[1]) };
  if (get && /\/api\/v3\/services$/.test(path)) return { type: 'observe-services' };
  if (get && (m = path.match(/\/organization\/(\d+)\/users$/))) return { type: 'observe-team', orgId: Number(m[1]) };
  if (req.method === 'PUT' && (m = path.match(/\/api\/marketplace\/organizations\/(\d+)\/public$/))) return { type: 'observe-public', orgId: Number(m[1]) };

  return null;
}

/** 404 = no such endpoint on this server — the only status the sample layer answers. */
export function missing(error: HttpErrorResponse): boolean {
  return error.status === 404;
}

function ok(req: HttpRequest<unknown>, body: any, source?: ProfileSampleSource): Observable<HttpEvent<unknown>> {
  if (source) noteProfileSample(source);
  return of(new HttpResponse({ status: 200, body, url: req.url }));
}

export const profileDemoBackendInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  if (!PROFILE_DEMO_ENABLED) return next(req);
  const kind = classify(req);
  if (!kind) return next(req);

  // inject() only works here, in the injection context — not inside a pipe callback (NG0203).
  const router = inject(Router);
  const navigating = router.getCurrentNavigation()?.finalUrl?.toString();
  if (!onProfileRoute(navigating ?? router.url)) return next(req);

  switch (kind.type) {
    case 'new-read':
      return next(req).pipe(
        catchError((error: HttpErrorResponse) => {
          if (!missing(error)) return throwError(() => error);
          if (!INVENTED_DATA_ENABLED) {
            // Real data only: capabilities, ratings and the team split are invented, so they keep
            // their 404 and the page shows its empty state. The portfolio still answers with the
            // pieces the reader added this session (it has no store yet), labelled only if any.
            if (kind.source !== 'portfolio') return throwError(() => error);
            const pieces = kind.seed();
            return ok(req, pieces, Array.isArray(pieces) && pieces.length ? kind.source : undefined);
          }
          return ok(req, kind.seed(), kind.source);
        }),
      );

    case 'new-write':
      return next(req).pipe(
        catchError((error: HttpErrorResponse) => (missing(error) ? ok(req, rememberWrite(kind.path, req.body)) : throwError(() => error))),
      );

    case 'fill-organization':
      return next(req).pipe(map(event => {
        if (!(event instanceof HttpResponse)) return event;
        const filled = fillOrganization(event.body, kind.orgId);
        if (!filled) return event;
        filled.used.forEach(noteProfileSample);
        return event.clone({ body: filled.body });
      }));

    case 'observe-services':
      return next(req).pipe(tap(event => { if (event instanceof HttpResponse) rememberServices(event.body); }));

    case 'observe-team':
      return next(req).pipe(tap(event => { if (event instanceof HttpResponse) rememberTeam(kind.orgId, event.body); }));

    case 'observe-public':
      // `isPublic` is not on GET /organization/{id} yet; remember what a successful write set.
      return next(req).pipe(tap(event => {
        if (event instanceof HttpResponse) {
          rememberWrite(`/organization/${kind.orgId}/listing`, { isPublic: req.params.get('value') === 'true' });
        }
      }));
  }
};
